function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { ChevronDown, Clear } from "@bolteu/kalep-react-icons";
import { MultiSelectChip } from "../_internal/MultiSelectChip";
import { cx } from "../../helpers/utils/cx";
import { noop, noopNull } from "../../helpers/utils/noop";
import { GhostButton, SpinnerIcon } from "../..";
import { DEFAULT_LOADING_SPINNER_ARIA_LABEL, DEFAULT_TOGGLE_BUTTON_ARIA_LABEL } from "./BaseComboBox";
const sizeStyles = {
  sm: "min-h-9 pl-3 pr-2 bolt-font-body-s-regular gap-2",
  md: "min-h-12 pl-3 pr-2 bolt-font-body-m-regular gap-2",
  lg: "min-h-14 pl-3 pr-2 bolt-font-body-m-regular gap-2"
};
const wrapperStyles = {
  idle: "border-special-nulled focus:border-action-primary text-primary",
  disabled: "border-neutral-secondary cursor-not-allowed text-tertiary bg-special-nulled placeholder-tertiary",
  error: "border-danger-primary text-primary"
};
const getInputTextStyles = (disabled, hasValue) => {
  if (disabled) {
    return "text-tertiary placeholder-tertiary";
  }
  if (hasValue) {
    return "text-primary placeholder-secondary";
  }
  return "text-secondary placeholder-secondary";
};
export function ComboBoxTriggerMultiline(props) {
  const {
    clearable,
    closeMenu,
    disabled,
    error,
    id,
    isOpen,
    generatedId,
    getDropdownProps,
    getInputProps,
    getOptionLabel,
    getToggleButtonProps,
    inputValue,
    inputWrapperRef,
    loading,
    loadingSpinnerAriaLabel = DEFAULT_LOADING_SPINNER_ARIA_LABEL,
    name,
    noResultsMessageId,
    onClear,
    onDelete,
    onInputChange,
    openMenu,
    placeholder,
    required,
    renderStartSlot,
    shouldShowNoResultsMessage,
    shouldRenderEndSlotIcon = true,
    size = "md",
    toggleButtonAriaLabel = DEFAULT_TOGGLE_BUTTON_ARIA_LABEL,
    toggleButtonRef,
    value
  } = props;
  const inputRef = React.useRef(null);
  const chipRefs = React.useRef({});
  const isValueArray = Array.isArray(value);
  const chipGap = React.useMemo(() => size === "sm" ? 4 : 6, [size]);
  const loadingSpinnerId = id ?? `kalep-combo-box-loading-spinner-${generatedId}`;
  const hasValue = isValueArray ? Boolean(value.length) : Boolean(value);
  const hasMoreThanOneChip = isValueArray && value.length > 1;
  if (isValueArray) {
    value.forEach(option => {
      if (option.value && !chipRefs.current[option.value]) {
        chipRefs.current[option.value] = chipRefs.current[option.value] || /*#__PURE__*/React.createRef();
      }
    });
  }
  const ddProps = getDropdownProps?.(inputRef);
  const inputA11yProps = getInputProps(_objectSpread(_objectSpread({
    ref: inputRef,
    disabled,
    onChange: onInputChange
  }, ddProps), {}, {
    onKeyDown: e => {
      ddProps?.onKeyDown?.(e);
      if (inputValue.length === 0 && e.key === "Backspace" && isValueArray && value.length > 0) {
        onDelete?.(value.length - 1); // Remove last selected item
      }
      if (e.key === "Escape") {
        inputRef.current?.blur();
      }
    }
  }));
  if (loading) {
    inputA11yProps["aria-labelledby"] = `${inputA11yProps["aria-labelledby"]} ${loadingSpinnerId}`;
  }
  if (shouldShowNoResultsMessage) {
    inputA11yProps["aria-labelledby"] = `${inputA11yProps["aria-labelledby"]} ${noResultsMessageId}`;
  }
  const handleClearButtonClick = React.useCallback(e => {
    onClear?.();
    e.preventDefault();
    e.stopPropagation();
  }, [onClear]);
  const onClick = React.useCallback(e => {
    if (disabled) {
      return;
    }
    if (!toggleButtonRef?.current?.contains(e.target)) {
      openMenu();
    }
    if (toggleButtonRef?.current?.contains(e.target) && isOpen) {
      closeMenu();
    }
  }, [openMenu, closeMenu, isOpen, toggleButtonRef, disabled]);
  const renderEndSlot = React.useCallback(() => {
    if (props.renderEndSlot) {
      return /*#__PURE__*/React.createElement("div", {
        className: cx("shrink-0 flex items-center max-w-6 gap-1")
      }, props.renderEndSlot(props, noopNull));
    }
    const showClearButton = !disabled && clearable && hasMoreThanOneChip;
    return /*#__PURE__*/React.createElement("div", {
      className: cx("flex items-center justify-end flex-shrink-0", size !== "sm" && "gap-1")
    }, /*#__PURE__*/React.createElement(GhostButton, {
      onClick: handleClearButtonClick,
      overrideClassName: cx("group/clear", "w-6 mx-1 flex-shrink-0 z-[60] invisible", showClearButton && "group-hover/trigger:visible"),
      tabIndex: -1
    }, /*#__PURE__*/React.createElement("div", {
      className: cx(isOpen ? "flex" : "hidden", "group-hover/trigger:flex", "justify-center items-center")
    }, /*#__PURE__*/React.createElement(Clear, {
      "aria-hidden": "true",
      className: cx("text-tertiary group-hover/clear:text-secondary"),
      size: size === "sm" ? "md" : "lg"
    }))), loading && /*#__PURE__*/React.createElement(SpinnerIcon, {
      id: loadingSpinnerId,
      "aria-label": loadingSpinnerAriaLabel,
      className: cx("flex-none mx-1 animate-spin text-action-secondary"),
      size: size,
      role: "status"
    }), !loading && shouldRenderEndSlotIcon && /*#__PURE__*/React.createElement("button", _extends({
      type: "button"
    }, getToggleButtonProps({
      ref: toggleButtonRef,
      "aria-label": toggleButtonAriaLabel
    }), {
      className: cx("border-0 bg-transparent cursor-pointer m-0 p-0 flex items-center", disabled && "cursor-not-allowed"),
      disabled: disabled
    }), /*#__PURE__*/React.createElement(ChevronDown, {
      className: cx("flex-none transition-transform m-1", disabled ? "text-tertiary" : "text-primary", isOpen && "rotate-180"),
      size: size === "sm" ? "md" : "lg"
    })));
  }, [props, disabled, clearable, hasMoreThanOneChip, size, handleClearButtonClick, isOpen, loading, loadingSpinnerId, loadingSpinnerAriaLabel, shouldRenderEndSlotIcon, getToggleButtonProps, toggleButtonRef, toggleButtonAriaLabel]);
  return /*#__PURE__*/React.createElement("div", {
    ref: inputWrapperRef,
    className: cx("group/trigger", "flex justify-end items-center w-full", "border-2 border-solid rounded-md", "outline-none overflow-hidden", "bg-neutral-secondary caret-border-action-primary", "ease-linear duration-100", disabled ? "cursor-not-allowed" : "cursor-text", disabled && wrapperStyles.disabled, error && wrapperStyles.error, !disabled && !error && wrapperStyles.idle, sizeStyles[size], !error && isOpen && "border-action-primary bg-special-nulled"),
    onClick: onClick,
    onKeyPress: noop,
    role: "textbox",
    tabIndex: -1
  }, typeof renderStartSlot === "function" && /*#__PURE__*/React.createElement("div", {
    className: cx("flex items-center max-w-6")
  }, renderStartSlot(props, noopNull), " "), /*#__PURE__*/React.createElement("div", {
    className: cx("w-full min-w-0")
  }, /*#__PURE__*/React.createElement("div", {
    className: cx("relative w-full flex flex-1 items-center transition-transform", "min-w-0",
    // Preventing overflowing flex container - https://stackoverflow.com/questions/36230944/prevent-flex-items-from-overflowing-a-container
    "flex-wrap overflow-visible", "py-2"),
    style: {
      gap: chipGap
    }
  }, hasValue && Array.isArray(value) ? /*#__PURE__*/React.createElement(React.Fragment, null, value.map((option, i) => /*#__PURE__*/React.createElement(MultiSelectChip, {
    key: option.value,
    ref: option.value ? chipRefs.current[option.value] : undefined,
    label: getOptionLabel(option),
    disabled: disabled,
    size: size
    // eslint-disable-next-line react/jsx-no-bind -- Needed here to grab index
    ,
    onDelete: () => onDelete?.(i)
  }))) : null, /*#__PURE__*/React.createElement("input", _extends({
    ref: inputRef,
    disabled: disabled,
    name: name,
    onChange: onInputChange,
    onFocus: openMenu,
    className: cx("block bg-special-nulled border-none outline-none p-0", size === "sm" ? "bolt-font-body-s-regular h-6" : "bolt-font-body-m-regular h-7", disabled ? "cursor-not-allowed" : "cursor-text", "min-w-12 w-full flex-1 truncate", getInputTextStyles(disabled, !!inputValue.length)),
    placeholder: placeholder,
    type: "text",
    "aria-required": required
  }, inputA11yProps)))), renderEndSlot());
}